import { Radio, Wifi, Satellite, Bluetooth, Signal, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

export const ConnectivitySection = () => {
  const { t } = useLanguage();

  const networks = [
    {
      name: t('connectivity.lorawan'),
      description: t('connectivity.lorawanDesc'),
      icon: Radio,
      features: [t('connectivity.lorawanF1'), t('connectivity.lorawanF2'), t('connectivity.lorawanF3')],
      link: '/connectivity#lorawan',
    },
    {
      name: t('connectivity.sigfox'),
      description: t('connectivity.sigfoxDesc'),
      icon: Signal,
      features: [t('connectivity.sigfoxF1'), t('connectivity.sigfoxF2'), t('connectivity.sigfoxF3')],
      link: '/connectivity#sigfox',
    },
    {
      name: t('connectivity.nbiot'),
      description: t('connectivity.nbiotDesc'),
      icon: Wifi,
      features: [t('connectivity.nbiotF1'), t('connectivity.nbiotF2'), t('connectivity.nbiotF3')],
      link: '/connectivity#nbiot',
    },
    {
      name: t('connectivity.ble'),
      description: t('connectivity.bleDesc'),
      icon: Bluetooth,
      features: [t('connectivity.bleF1'), t('connectivity.bleF2'), t('connectivity.bleF3')],
      link: '/connectivity#ble',
    },
    {
      name: t('connectivity.satellite'),
      description: t('connectivity.satelliteDesc'),
      icon: Satellite,
      features: [t('connectivity.satelliteF1'), t('connectivity.satelliteF2'), t('connectivity.satelliteF3')],
      link: '/connectivity#satellite',
    },
  ];

  return (
    <section className="py-20 md:py-32 bg-slate-900" data-testid="connectivity-section">
      <div className="container-0g">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{t('connectivity.label')}</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">{t('connectivity.title')}</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t('connectivity.subtitle')}
          </p>
        </div>

        {/* Networks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {networks.map((network, index) => {
            const Icon = network.icon;
            return (
              <Link
                key={index}
                to={network.link}
                className="text-center group p-6 bg-slate-800 border border-slate-700 rounded-sm hover:border-teal-500 transition-colors cursor-pointer"
                data-testid={`network-${index}`}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-sm bg-slate-700 flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-600 transition-colors">
                  <Icon className="h-7 w-7 text-slate-400 group-hover:text-white transition-colors" />
                </div>
                
                {/* Name */}
                <h3 className="text-lg font-semibold text-white mb-2">{network.name}</h3>
                
                {/* Description */}
                <p className="text-sm text-slate-400 mb-4">{network.description}</p>
                
                {/* Features */}
                <div className="space-y-1 mb-4">
                  {network.features.map((feature, fIndex) => (
                    <p key={fIndex} className="text-xs text-slate-500">{feature}</p>
                  ))}
                </div>
                
                {/* Learn more link */}
                <div className="flex items-center justify-center text-teal-400 font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>{t('solutions.learnMore')}</span>
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
